import { Form, Button } from "react-bootstrap";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState } from "react";
import * as api from "../api/api";
import Loading from "../Components/Loading";
import { Snackbar, Alert } from "@mui/material";
import TimezoneSelect from "react-timezone-select";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import checkAuthorization from "../config/checkAuth";
import { useLanguage } from "../Components/Localization/LanguageContext";
import strings from "../Components/Localization/strings";

export default function AdminSettings() {
  const { language } = useLanguage();
  const pageTitle = "AdminSettings";
  const [settingsInfo, setSettingsInfo] = useState({
    deleteUnsavedActivitiesDurationHours: 0,
    isOverheadPermittedAccess: false,
    isRestrictedMode: false,
    isTotalTimeHidden: false,
    tenantId: 0,
    timeZoneId: "string",
    trackedActivitiesDisplayDurationMonths: 0,
    houseSettingDTO: {
      isHouse: false,
      isHousePopUp: false,
    },
    jiraSettingDto: {
      isJira: false,
      isJiraPopUp: false,
      jiraTicketNumberPrefix: null,
      jiraURL: null,
      jiraSuffix: null,
      jiraCaseCreationUrl: null,
      jiraPhoneFieldId: null,
      jiraMarketNumberFieldId: null,
    },
  });
  const [snackbarData, setSnackbarData] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [isHoursValid, setIsHoursValid] = useState(true);
  const navigate = useNavigate();

  const getSettings = () => {
    setLoading(true);
    api
      .getAppSettingsOfTenantByTenantId(
        JSON.parse(sessionStorage.getItem("userTenant"))?.tenantId
      )
      .then((response) => {
        setSettingsInfo(response.data.object);
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  useEffect(() => {
    checkAuthorization("SETTINGS").then((result) => {
      if (result) {
        getSettings();
      } else {
        navigate("/unauthorized");
      }
    });
  }, []);

  const submitForm = (e) => {
    e.preventDefault();
    if (!isHoursValid) {
      setSnackbarData({ ...snackbarData, show: true });
      return;
    }

    api
      .editAppSettingsForTenant(settingsInfo)
      .then((response) => {
        if (response.data.statusCode == 500) {
          setSnackbarData({
            ...snackbarData,
            message: response.data.message,
            severity: "error",
            show: true,
          });
          return;
        } else if (response.data.statusCode == 400) {
          setSnackbarData({
            ...snackbarData,
            message: response.data.message,
            severity: "error",
            show: true,
          });
          return;
        } else {
          setSnackbarData({
            ...snackbarData,
            message: response.data.message,
            severity: "success",
            show: true,
          });
          setSettingsInfo(response.data.object);
        }
      })
      .catch((e) => {
        console.log(e);
        setSnackbarData({
          ...snackbarData,
          message: e.data.message,
          severity: "error",
          show: true,
        });
      });
  };

  return isLoading ? (
    <Loading></Loading>
  ) : (
    <div className="main-content-container">
      <div className="main-content">
        <div>
          <h6 className="main-content__title">
            {strings[pageTitle][language].pageName}
          </h6>
          <hr className="main-content-title__hr" />

          <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            open={snackbarData.show}
            autoHideDuration={3000}
            onClose={() => setSnackbarData({ ...snackbarData, show: false })}
          >
            <Alert severity={snackbarData.severity} sx={{ width: "100%" }}>
              {snackbarData.message}
            </Alert>
          </Snackbar>
          <Form onSubmit={submitForm}>
            <div style={{ width: "95%", marginLeft: "4em", marginTop: "2em" }}>
              <Form.Group className="mb-3 form-inline">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="isTotalTimeHidden"
                    >
                      {strings[pageTitle][language].total}
                    </Form.Label>
                    <Tooltip
                      title={strings[pageTitle][language].totalHint}
                      arrow
                    >
                      <IconButton>
                        <InfoIcon style={{ color: "grey" }}></InfoIcon>
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="col-md-2">
                    <Form.Check
                      id="isTotalTimeHidden"
                      style={{ transform: "scale(1.25)" }}
                      checked={settingsInfo.isTotalTimeHidden}
                      onChange={(e) => {
                        setSettingsInfo((prevSettingsInfo) => ({
                          ...prevSettingsInfo,
                          isTotalTimeHidden: e.target.checked,
                        }));
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group className="mb-3 form-inline">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="isRestrictedMode"
                    >
                      {strings[pageTitle][language].restricted}
                    </Form.Label>
                    <Tooltip
                      title={strings[pageTitle][language].restrictedHint}
                      arrow
                    >
                      <IconButton>
                        <InfoIcon style={{ color: "grey" }}></InfoIcon>
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="col-md-2">
                    <Form.Check
                      id="isRestrictedMode"
                      style={{ transform: "scale(1.25)" }}
                      defaultChecked={settingsInfo.isRestrictedMode}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          isRestrictedMode: e.target.checked,
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group className="mb-3 form-inline">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="isOverheadPermittedAccess"
                    >
                      {strings[pageTitle][language].overhead}
                    </Form.Label>
                    <Tooltip
                      title={strings[pageTitle][language].overheadHint}
                      arrow
                    >
                      <IconButton>
                        <InfoIcon style={{ color: "grey" }}></InfoIcon>
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="col-md-2">
                    <Form.Check
                      id="isOverheadPermittedAccess"
                      style={{ transform: "scale(1.25)" }}
                      defaultChecked={settingsInfo.isOverheadPermittedAccess}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          isOverheadPermittedAccess: e.target.checked,
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group className="mb-3 form-inline">
                <div>
                  <Form.Label className="row align-items-center">
                    <div className="col-md-4">
                      {strings[pageTitle][language].timeZone}{" "}
                    </div>
                    <div
                      className="col-md-4"
                      style={{
                        transform: "scale(1.15)",
                        zIndex: 1,
                        position: "relative",
                      }}
                    >
                      <TimezoneSelect
                        id="timeZone"
                        displayValue="UTC"
                        value={settingsInfo.timeZoneId}
                        onChange={(timezone) => {
                          setSettingsInfo({
                            ...settingsInfo,
                            timeZoneId: timezone.value,
                          });
                        }}
                        style={{ zIndex: 1, position: "relative" }}
                      />
                    </div>
                  </Form.Label>
                </div>
              </Form.Group>
              <Form.Group className="mb-3 form-inline">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="trackedActivitiesDisplayDurationMonths"
                    >
                      {strings[pageTitle][language].display}
                    </Form.Label>
                    <Tooltip
                      title={strings[pageTitle][language].displayHint}
                      arrow
                    >
                      <IconButton>
                        <InfoIcon style={{ color: "grey" }}></InfoIcon>
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="col-md-2">
                    <Form.Select
                      value={
                        settingsInfo.trackedActivitiesDisplayDurationMonths
                      }
                      style={{ transform: "scale(1.30)" }}
                      size="sm"
                      id="trackedActivitiesDisplayDurationMonths"
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          trackedActivitiesDisplayDurationMonths:
                            e.target.value,
                        });
                      }}
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                    </Form.Select>
                  </div>
                  <div className="col-md-2" style={{ marginLeft: 15 }}>
                    <Form.Label htmlFor="trackedActivitiesDisplayDurationMonths">
                      {strings[pageTitle][language].months}
                    </Form.Label>
                  </div>
                </div>
              </Form.Group>
              <Form.Group className="mb-3 form-inline">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="hoursDuration"
                    >
                      {strings[pageTitle][language].unsaved}
                    </Form.Label>
                    <Tooltip
                      title={strings[pageTitle][language].unsavedHint}
                      arrow
                    >
                      <IconButton>
                        <InfoIcon style={{ color: "grey" }}></InfoIcon>
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="col-md-2" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.15)" }}
                      type="text"
                      id="hoursDuration"
                      defaultValue={
                        settingsInfo.deleteUnsavedActivitiesDurationHours
                      }
                      isInvalid={!isHoursValid}
                      onChange={(e) => {
                        if (
                          e.target.value < 8 ||
                          e.target.value > 12 ||
                          e.target.value === ""
                        ) {
                          setSnackbarData({
                            ...snackbarData,
                            message: "Hours should be between 8 to 12",
                            severity: "error",
                            show: true,
                          });
                          setIsHoursValid(false);
                        } else {
                          setIsHoursValid(true);
                          setSnackbarData({
                            ...snackbarData,
                            show: false,
                          });
                          setSettingsInfo({
                            ...settingsInfo,
                            deleteUnsavedActivitiesDurationHours:
                              e.target.value,
                          });
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <Form.Label htmlFor="hoursDuration">
                      {strings[pageTitle][language].hours}
                    </Form.Label>
                  </div>
                </div>
              </Form.Group>
              <Form.Group>
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="isVoiceChannel"
                    >
                      {strings[pageTitle][language].allowVoice}
                    </Form.Label>
                  </div>
                  <div className="col-md-2" style={{ paddingLeft: 0 }}>
                    <Form.Check
                      style={{ transform: "scale(1.15)" }}
                      type="checkbox"
                      id="isVoiceChannel"
                      checked={settingsInfo.isVoiceChannel}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          isVoiceChannel: e.target.checked,
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={
                  !settingsInfo.isVoiceChannel &&
                  (!settingsInfo.clientId || settingsInfo.clientId === "")
                }
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="clientId"
                    >
                      Genesys Client ID
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.10)" }}
                      type="text"
                      id="clientId"
                      value={settingsInfo.clientId}
                      disabled={!settingsInfo.isVoiceChannel}
                      required={settingsInfo.isVoiceChannel}
                      pattern="^([0-9A-Za-z]{8}-[0-9A-Za-z]{4}-[0-9A-Za-z]{4}-[0-9A-Za-z]{4}-[0-9A-Za-z]{12})?$"
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          clientId: e.target.value,
                        });
                        e.target.setCustomValidity("");
                      }}
                      onInvalid={(e) => {
                        e.preventDefault();
                        console.log("invalid");

                        // test the pattern again
                        var patt = new RegExp(
                          "^([0-9A-Za-z]{8}-[0-9A-Za-z]{4}-[0-9A-Za-z]{4}-[0-9A-Za-z]{4}-[0-9A-Za-z]{12})?$"
                        );
                        console.log(patt.test(e.target.value));

                        e.target.setCustomValidity(
                          "Genesys Client ID should be 8-4-4-4-12 alphanumeric characters"
                        );
                        e.target.reportValidity();
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={!settingsInfo.isVoiceChannel && !settingsInfo.envUrl}
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label style={{ marginRight: "5px" }} htmlFor="envUrl">
                      Genesys Environment URL
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.10)" }}
                      type="text"
                      id="envUrl"
                      value={settingsInfo.envUrl}
                      disabled={!settingsInfo.isVoiceChannel}
                      required={
                        settingsInfo.isVoiceChannel &&
                        settingsInfo.clientId !== ""
                      }
                      pattern="^mypurecloud.+$"
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          envUrl: e.target.value,
                        });
                        e.target.setCustomValidity("");
                      }}
                      onInvalid={(e) => {
                        e.preventDefault();
                        e.target.setCustomValidity(
                          "Follow the pattern of mypurecloud.*"
                        );
                        e.target.reportValidity();
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={!settingsInfo.isVoiceChannel && !settingsInfo.appName}
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="appName"
                    >
                      Genesys App Name
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.10)" }}
                      type="text"
                      required={
                        settingsInfo.isVoiceChannel &&
                        settingsInfo.clientId !== ""
                      }
                      id="appName"
                      value={settingsInfo.appName}
                      disabled={!settingsInfo.isVoiceChannel}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          appName: e.target.value,
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>

              <Form.Group>
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="houseDatabaseEnabled"
                    >
                      House Database Enabled
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Check
                      style={{ transform: "scale(1.10)" }}
                      type="checkbox"
                      id="houseDatabaseEnabled"
                      checked={settingsInfo.houseSettingDTO?.isHouse || false}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          houseSettingDTO: {
                            ...settingsInfo.houseSettingDTO,
                            isHouse: e.target.checked,
                          },
                        });
                        console.log(settingsInfo);
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group hidden={!settingsInfo.houseSettingDTO?.isHouse}>
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="houseOpenInPopup"
                    >
                      Open house view in popup
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Check
                      style={{ transform: "scale(1.10)" }}
                      type="checkbox"
                      id="houseOpenInPopup"
                      checked={
                        settingsInfo.houseSettingDTO?.isHousePopUp || false
                      }
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          houseSettingDTO: {
                            ...settingsInfo.houseSettingDTO,
                            isHousePopUp: e.target.checked,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group>
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="isVoiceChannel"
                    >
                      {"Jira enabled"}
                    </Form.Label>
                  </div>
                  <div className="col-md-2" style={{ paddingLeft: 0 }}>
                    <Form.Check
                      style={{ transform: "scale(1.15)" }}
                      type="checkbox"
                      id="jiraSettingDto.isJira"
                      checked={settingsInfo.jiraSettingDto?.isJira}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          jiraSettingDto: {
                            ...settingsInfo.jiraSettingDto,
                            isJira: e.target.checked,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={settingsInfo.jiraSettingDto?.isJira === false}
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="isJiraPopUp"
                    >
                      {"Jira opens in pop up"}
                    </Form.Label>
                  </div>
                  <div className="col-md-2" style={{ paddingLeft: 0 }}>
                    <Form.Check
                      style={{ transform: "scale(1.15)" }}
                      type="checkbox"
                      id="jiraSettingDto.isJiraPopUp"
                      checked={settingsInfo.jiraSettingDto?.isJiraPopUp}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          jiraSettingDto: {
                            ...settingsInfo.jiraSettingDto,
                            isJiraPopUp: e.target.checked,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={settingsInfo.jiraSettingDto?.isJira === false}
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="jiraTicketNumberPrefix"
                    >
                      {"Jira Ticket Number Prefix"}
                    </Form.Label>
                  </div>
                  <div className="col-md-2" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.10)" }}
                      type="text"
                      id="appName"
                      value={
                        settingsInfo.jiraSettingDto?.jiraTicketNumberPrefix
                      }
                      disabled={!settingsInfo.jiraSettingDto?.isJira}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          jiraSettingDto: {
                            ...settingsInfo.jiraSettingDto,
                            jiraTicketNumberPrefix: e.target.value,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={settingsInfo.jiraSettingDto?.isJira === false}
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="jiraURL"
                    >
                      Jira URL
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.10)" }}
                      type="text"
                      id="jiraURL"
                      value={settingsInfo.jiraSettingDto?.jiraURL}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          jiraSettingDto: {
                            ...settingsInfo.jiraSettingDto,
                            jiraURL: e.target.value,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={settingsInfo.jiraSettingDto?.isJira === false}
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="jiraURL"
                    >
                      Jira Suffix URL
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.10)" }}
                      type="text"
                      id="jiraSuffixURL"
                      value={settingsInfo.jiraSettingDto?.jiraSuffix}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          jiraSettingDto: {
                            ...settingsInfo.jiraSettingDto,
                            jiraSuffix: e.target.value,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={settingsInfo.jiraSettingDto?.isJira === false}
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="jiraURL"
                    >
                      Jira Case Creation URL
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.10)" }}
                      type="text"
                      id="jiraCaseCreationUrl"
                      value={settingsInfo.jiraSettingDto?.jiraCaseCreationUrl}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          jiraSettingDto: {
                            ...settingsInfo.jiraSettingDto,
                            jiraCaseCreationUrl: e.target.value,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={settingsInfo.jiraSettingDto?.isJira === false}
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="jiraURL"
                    >
                      Jira Phone Field ID
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.10)" }}
                      type="text"
                      id="jiraPhoneFieldId"
                      value={settingsInfo.jiraSettingDto?.jiraPhoneFieldId}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          jiraSettingDto: {
                            ...settingsInfo.jiraSettingDto,
                            jiraPhoneFieldId: e.target.value,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Form.Group
                hidden={settingsInfo.jiraSettingDto?.isJira === false}
              >
                <div
                  className="row align-items-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="col-md-4">
                    <Form.Label
                      style={{ marginRight: "5px" }}
                      htmlFor="jiraURL"
                    >
                      Jira Market Number Field ID
                    </Form.Label>
                  </div>
                  <div className="col-md-4" style={{ paddingLeft: 0 }}>
                    <Form.Control
                      style={{ transform: "scale(1.10)" }}
                      type="text"
                      id="jiraMarketNumberFieldId"
                      value={settingsInfo.jiraSettingDto?.jiraMarketNumberFieldId}
                      onChange={(e) => {
                        setSettingsInfo({
                          ...settingsInfo,
                          jiraSettingDto: {
                            ...settingsInfo.jiraSettingDto,
                            jiraMarketNumberFieldId: e.target.value,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
              <Button
                onClick={() => {
                  setSettingsInfo({
                    ...settingsInfo,
                    isVoiceChannel: false,
                    clientId: "",
                    envUrl: "",
                    appName: "",
                  });
                }}
                type="submit"
                style={{ marginTop: 20, marginLeft: 100 }}
                hidden={!settingsInfo.isVoiceChannel} // only show button if voice channel is enabled
                variant="danger"
              >
                Remove voice channel configuration and delete all data
              </Button>
            </div>
            <hr className="main-content-title__hr" style={{ width: "100%" }} />
            <div className="d-flex align-items-center justify-content-end">
              <div className="d-flex align-items-end gap-2 ">
                <Button
                  variant="outline-secondary"
                  style={{ width: 100, borderRadius: "1.5em" }}
                  onClick={getSettings}
                >
                  {strings[pageTitle][language].cancel}
                </Button>
                <Button
                  variant="outline-primary"
                  className="button-round"
                  style={{ width: 100 }}
                  type="submit"
                >
                  {strings[pageTitle][language].save}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}
